import { CalendarPlottedEvent } from '../../services/enterpriseCalendarsApi';
import { CalendarEventChip } from './CalendarEventChip';
import { plottedEventOccursOnDay, formatEventTimeLabel } from './calendarRecurrenceUtils';
import { DAY_LABELS, formatHourLabel, getReadableTextColor, isSameDay, parseTimeToMinutes } from './calendarUtils';

type CalendarDayViewProps = {
  day: Date;
  events: CalendarPlottedEvent[];
  onDayClick?: (day: Date, hour?: number) => void;
  onEventClick?: (event: CalendarPlottedEvent) => void;
};

const START_HOUR = 7;
const END_HOUR = 21;
const HOUR_HEIGHT = 48;
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, index) => index + START_HOUR);

export function CalendarDayView({ day, events, onDayClick, onEventClick }: CalendarDayViewProps) {
  const today = new Date();
  const dayEvents = events.filter((event) => plottedEventOccursOnDay(event, day));
  const allDayEvents = dayEvents.filter((event) => event.isAllDay);
  const timedEvents = dayEvents.filter(
    (event) => !event.isAllDay && event.startTime && event.endTime,
  );
  const isToday = isSameDay(day, today);
  const gridHeight = HOURS.length * HOUR_HEIGHT;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex h-12 w-12 items-center justify-center rounded-full text-lg font-semibold ${
              isToday ? 'bg-primary-600 text-white' : 'bg-white text-slate-800 ring-1 ring-slate-200'
            }`}
          >
            {day.getDate()}
          </span>
          <div>
            <p className="text-sm font-semibold text-slate-900">{DAY_LABELS[day.getDay()]}</p>
            <p className="text-xs text-slate-500">
              {day.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
      </div>

      <div className="border-b border-slate-200 px-4 py-3">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Dia inteiro</p>
        <div className="space-y-1">
          {allDayEvents.length === 0 ? (
            <button
              type="button"
              className="w-full rounded-lg border border-dashed border-slate-200 px-3 py-4 text-left text-sm text-slate-400 hover:bg-slate-50"
              onClick={() => onDayClick?.(day)}
            >
              Nenhum evento de dia inteiro
            </button>
          ) : (
            allDayEvents.map((event) => (
              <CalendarEventChip key={`${event.calendarId}-${event.id}`} event={event} onClick={onEventClick} />
            ))
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="grid grid-cols-[64px_1fr]">
          <div>
            {HOURS.map((hour) => (
              <div
                key={hour}
                className="border-b border-slate-100 px-3 text-right text-xs text-slate-400"
                style={{ height: HOUR_HEIGHT }}
              >
                <span className="-mt-2 inline-block">{formatHourLabel(hour)}</span>
              </div>
            ))}
          </div>

          <div className="relative border-l border-slate-100" style={{ height: gridHeight }}>
            {HOURS.map((hour) => (
              <button
                key={hour}
                type="button"
                className="absolute left-0 right-0 border-b border-slate-100 hover:bg-slate-50/80"
                style={{
                  top: (hour - START_HOUR) * HOUR_HEIGHT,
                  height: HOUR_HEIGHT,
                }}
                onClick={() => onDayClick?.(day, hour)}
                aria-label={`Horário ${formatHourLabel(hour)}`}
              />
            ))}

            {timedEvents.map((event) => {
              const position = getTimedEventPosition(event);
              if (!position) return null;

              const timeLabel = formatEventTimeLabel(event);

              return (
                <button
                  key={`${event.calendarId}-${event.id}-timed`}
                  type="button"
                  className="absolute left-1 right-1 overflow-hidden rounded px-2 py-1 text-left text-xs font-medium shadow-sm"
                  style={{
                    top: position.top,
                    height: position.height,
                    backgroundColor: event.color,
                    color: getReadableTextColor(event.color),
                    zIndex: 1,
                  }}
                  title={`${event.name}${timeLabel ? ` (${timeLabel})` : ''} · ${event.calendarName}`}
                  onClick={(clickEvent) => {
                    clickEvent.stopPropagation();
                    onEventClick?.(event);
                  }}
                >
                  <span className="block truncate font-semibold">{event.name}</span>
                  {timeLabel ? <span className="block truncate text-[10px] opacity-90">{timeLabel}</span> : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function getTimedEventPosition(event: CalendarPlottedEvent): { top: number; height: number } | null {
  if (!event.startTime || !event.endTime) {
    return null;
  }

  const startMinutes = parseTimeToMinutes(event.startTime);
  const endMinutes = parseTimeToMinutes(event.endTime);
  const gridStartMinutes = START_HOUR * 60;
  const gridEndMinutes = END_HOUR * 60;

  if (endMinutes <= gridStartMinutes || startMinutes >= gridEndMinutes) {
    return null;
  }

  const clampedStart = Math.max(startMinutes, gridStartMinutes);
  const clampedEnd = Math.min(endMinutes, gridEndMinutes);
  const top = ((clampedStart - gridStartMinutes) / 60) * HOUR_HEIGHT;
  const height = Math.max(((clampedEnd - clampedStart) / 60) * HOUR_HEIGHT, 20);

  return { top, height };
}
